/**
 * Ход работ по площади (волна 2а, решения владельца №645 «про землю —
 * гектары», сервер — №660–664).
 *
 * Сервер отдаёт рядом со счётом обязательств поле `area_work`
 * (`WorkAreaOut`, `basis: "area"`): гектары работ, где у каждой работы вес —
 * площадь самой работы на поле (план — размещение, карантин — земля контура, полив — доля посева). Доли (`done_pct`, `overdue_pct`…)
 * считает сервер как отношение сумм — фронт их НЕ пересчитывает, а только
 * переводит микрогектары в гектары и выбирает подпись.
 *
 * «Гектары работ» — не «земля»: поле с тремя работами после срока даёт три
 * своих площади. Поэтому абсолютные числа подписаны «га работ», а доли —
 * «% площади» (площади работ). Нет площади (сервер вернул `null` или
 * знаменатель пуст) — экран говорит «площадь не указана», а не «0».
 */
import type { Schemas } from '../../api/client';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';
import { num, pct } from './format';

export type WorkArea = Schemas['WorkAreaOut'];

const m = defineMessages({
  areaShare: {
    id: 'common.ui.areaWork.areaShare',
    defaultMessage: '% площади',
    description: 'Единица главной доли хода работ — процент площади работ (рядом с числом, в подписи KPI/колонки); коротко',
  },
  byCount: {
    id: 'common.ui.areaWork.byCount',
    defaultMessage: 'по числу работ',
    description: 'Подпись второго слоя доли: считано по числу работ, а не по площади (мелко, после процента)',
  },
  workHa: {
    id: 'common.ui.areaWork.workHa',
    defaultMessage: 'га работ',
    description: 'Единица суммы гектаров по работам на полях (поле с тремя работами считается трижды); не путать с «га земли»; коротко',
  },
  noArea: {
    id: 'common.ui.areaWork.noArea',
    defaultMessage: 'площадь не указана',
    description: 'Вместо числа, когда у среза нет площади для расчёта долей; честное «нет данных», а не 0',
  },
  areaShareText: {
    id: 'common.ui.areaWork.areaShareText',
    defaultMessage: '{value} площади',
    description: 'Главная доля хода работ в тексте: «78,4 % площади»; {value} — уже отформатированный процент',
  },
  countShareText: {
    id: 'common.ui.areaWork.countShareText',
    defaultMessage: '{value} по числу работ',
    description: 'Второй слой доли мелким текстом: «77,2 % по числу работ»; {value} — отформатированный процент',
  },
});

/** Единица доли по площади — всегда перед счётной. */
export function areaShareLabel(): string {
  return tr(m.areaShare);
}
/** Подпись второго слоя — доля по числу работ. */
export function byCountLabel(): string {
  return tr(m.byCount);
}
/** Единица абсолютных гектаров работ («га работ»). */
export function workHaUnit(): string {
  return tr(m.workHa);
}
/** Честное «нет площади». */
export function noArea(): string {
  return tr(m.noArea);
}

const MICRO = 1_000_000;

/** Микрогектары → гектары; `null` остаётся `null`. */
export function haOf(microha: number | null | undefined): number | null {
  return typeof microha === 'number' && Number.isFinite(microha) ? microha / MICRO : null;
}

/** Есть ли у среза площадь, от которой можно считать доли. */
export function hasArea(aw: WorkArea | null | undefined): aw is WorkArea {
  return !!aw && typeof aw.total_microha === 'number' && aw.total_microha > 0;
}

/** Доля выполненного по площади, %; `null` — площадь не указана. */
export function areaDonePct(aw: WorkArea | null | undefined): number | null {
  return hasArea(aw) && typeof aw.done_pct === 'number' ? aw.done_pct : null;
}

/** Изменение доли выполненного по площади к другой точке, п.п. */
export function areaDoneDelta(now: WorkArea | null | undefined, then: WorkArea | null | undefined): number | null {
  const a = areaDonePct(now);
  const b = areaDonePct(then);
  return a !== null && b !== null ? a - b : null;
}

/**
 * Три оси в гектарах для `DoneSplit` (`denominator: 'га работ'`): всего,
 * «с отчётом» (= выполнено + в процессе) и «в процессе». `null` — площади нет.
 */
export function areaSplit(aw: WorkArea | null | undefined): { total: number; reported: number; inProgress: number | null } | null {
  if (!hasArea(aw)) return null;
  const total = aw.total_microha! / MICRO;
  const done = haOf(aw.done_microha);
  const progress = haOf(aw.in_progress_microha);
  if (done === null) return null;
  return { total, reported: done + (progress ?? 0), inProgress: progress };
}

/** «78,4 % площади» — главная доля; без площади — «площадь не указана». */
export function areaShareText(value: number | null, digits = 1): string {
  return value === null ? noArea() : tr(m.areaShareText, { value: pct(value, digits) });
}

/** «77,2 % по числу работ» — второй слой мелко; `null` — пусто. */
export function countShareText(part: number | null, total: number | null, digits = 1): string | null {
  if (part === null || total === null || !(total > 0)) return null;
  return tr(m.countShareText, { value: pct((100 * part) / total, digits) });
}

/** Целые гектары: «42 014». */
export function haWhole(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : num(Math.round(value));
}
